import _ from "lodash";
import * as React from "react";
import { Button } from "web-ui/juicy";

import DisclosureTriangle from "./DisclosureTriangle";
import styles from "./SortableTable.module.scss";

export interface Column<T> {
  getCell: (n: T) => React.ReactNode;
  getValue?: ((n: T) => number) | ((n: T) => string);
  header?: React.ReactNode;
  isHidden?: boolean;
}

enum SortStatus {
  Ascending = "ascending",
  Descending = "descending",
}

interface Props<T> {
  columns: Column<T>[];
  data: T[];
}

const SortableTable = <T,>({ columns, data }: Props<T>) => {
  const [sortedData, setSortedData] = React.useState(data);
  const [sortColumnIndex, setSortColumnIndex] = React.useState(-1);
  const [sortStatus, setSortStatus] = React.useState<SortStatus>(
    SortStatus.Ascending,
  );

  // Reset sort whenever underlying data changes.
  React.useLayoutEffect(() => {
    setSortedData(data);
    setSortColumnIndex(-1);
    setSortStatus(SortStatus.Ascending);
  }, [data]);

  const visibleColumns = columns.filter(c => !c.isHidden);

  return (
    <div
      className={styles.grid}
      style={{
        gridTemplateColumns: Array(visibleColumns.length + 1)
          .fill("auto")
          .join(" "),
      }}
    >
      {visibleColumns.map((c, i) => {
        const columnSortStatus =
          sortColumnIndex === i ? sortStatus : SortStatus.Ascending;

        const handleClick = () => {
          if (!c.getValue) {
            throw Error("Unable to sort column without knowing value");
          }

          const nextSortStatus =
            columnSortStatus === SortStatus.Ascending
              ? SortStatus.Descending
              : SortStatus.Ascending;
          const nextSortedData = _.sortBy<T>(sortedData, c.getValue);

          if (nextSortStatus === SortStatus.Descending) {
            nextSortedData.reverse();
          }

          setSortColumnIndex(i);
          setSortStatus(nextSortStatus);
          setSortedData(nextSortedData);
        };

        return (
          <React.Fragment key={i}>
            <Button
              className={styles.header}
              onClick={handleClick}
              size="small"
              state={c.getValue === undefined ? "disabled" : "enabled"}
              style={{
                gridColumn: i + 1,
                gridRow: 1,
              }}
              title={
                c.header === "Patch"
                  ? "Note: This is the number of patched builds we made for a particular release, not the number of patched PRs that made it into the release"
                  : undefined
              }
              variant="link"
            >
              <span>{c.header}</span>
              {c.getValue ? (
                <DisclosureTriangle
                  direction={
                    columnSortStatus === SortStatus.Ascending ? "down" : "up"
                  }
                />
              ) : null}
            </Button>
            {sortedData.map((e, j, rows) => (
              <div
                className={styles.cell}
                key={j}
                style={{
                  borderBottom: j === rows.length - 1 ? 0 : undefined,
                  gridColumn: i + 1,
                  gridRow: j + 2,
                }}
              >
                {c.getCell(e)}
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SortableTable;
